import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { FormComponent } from '../../../../shared/widgets/form';
import { FormInterface } from '../../../../shared/interfaces';
import { DialogActionsEnum, DialogComponent, DialogConfigInterface } from '../../../../shared/components/dialog';

import {
    AuthApiService,
    ProductFieldNames,
    FundProductsService,
    FundProductTypesEnum,
    HttpStatusCodesEnum,
    ICreateProductRequest,
    ICreateProductRespoonse,
    OperatorInterface,
    OperatorsService,
} from '@spa/api';
import { Router } from '@angular/router';
import { AlertsService } from 'appShared/services';
import { PanelPaths } from 'appShared/paths';
import { CreateProductForm } from 'appEntries/panel/forms';
import * as moment from 'moment';
import { AlertTypesEnum } from 'appShared/enums';
import { Subscription } from 'rxjs';
import { SelectComponent } from 'appShared/components';
import { ValidatorFn, Validators } from '@angular/forms';
import { Helper } from 'appShared/classes';

@Component({
    selector: 'app-create-product',
    templateUrl: './create-product.component.html',
    styleUrls: ['./create-product.component.scss']
})
export class CreateProductComponent
    extends DialogComponent<CreateProductComponent>
    implements OnInit, OnDestroy, AfterViewInit, FormInterface {
    private operatorName: string;
    private operatorSubscription: Subscription;
    public createProductForm: CreateProductForm;
    @ViewChild('productTypeSelectInput', { static: false }) public productTypeSelectInput: SelectComponent;

    constructor(
        private readonly operatorsService: OperatorsService,
        private readonly authApiService: AuthApiService,
        private readonly fundProductsService: FundProductsService,
        private readonly router: Router,
        private readonly alertsService: AlertsService,
        @Inject(MAT_DIALOG_DATA) public readonly data: {
            config: DialogConfigInterface,
        },
        public readonly dialogRef: MatDialogRef<CreateProductComponent>,
    ) {
        super(dialogRef);

        this.config = data.config;
    }

    private getInputModel(): ICreateProductRequest {
        const type = +this.createProductForm.form.value[ProductFieldNames.Type];
        const code = this.createProductForm.form.value[ProductFieldNames.Code];
        const name = this.createProductForm.form.value[ProductFieldNames.Name];
        const nameBG = this.createProductForm.form.value[ProductFieldNames.NameBG];
        const version = this.createProductForm.form.value[ProductFieldNames.Version];
        const currency = this.createProductForm.form.value[ProductFieldNames.Currency];
        const nominalValue = +Helper.compactString(this.createProductForm.form.value[ProductFieldNames.NominalValue]);
        const durationInMonths = +this.createProductForm.form.value[ProductFieldNames.DurationInMonths];
        const issuedBy = this.createProductForm.form.value[ProductFieldNames.IssuedBy];
        const createdBy = this.operatorName;
        const issuedOn = moment(this.createProductForm.form.value[ProductFieldNames.IssuedOn]).toDate();

        const expiresOnValue = this.createProductForm.form.value[ProductFieldNames.ExpiresOn];
        const expiresOn = expiresOnValue ? moment(expiresOnValue).toDate() : null;

        return {
            type,
            code,
            name,
            nameBG,
            version,
            currency,
            nominalValue,
            durationInMonths,
            createdBy,
            issuedBy,
            issuedOn,
            expiresOn
        };
    }

    ngAfterViewInit(): void {
        this.productTypeSelectInput.registerOnChange((type: number) => {

            const validators: ValidatorFn[] = [];
            this.createProductForm.form.get(ProductFieldNames.NominalValue).clearValidators();

            if (type === FundProductTypesEnum.MutualFund) {
                this.createProductForm.controls.durationInMonths.disabled = true;
                this.createProductForm.controls.nominalValue.disabled = true;
                this.createProductForm.controls.nominalValue.resetErrors();
            }
            else if (type === FundProductTypesEnum.InvestmentCertificate) {
                this.createProductForm.controls.durationInMonths.disabled = false;
                this.createProductForm.controls.nominalValue.disabled = false;
                validators.push(Validators.required);
            }
            else if (type === FundProductTypesEnum.Bonds) {
                this.createProductForm.controls.durationInMonths.disabled = false;
                this.createProductForm.controls.nominalValue.disabled = false;
                validators.push(Validators.required);
            }

            this.createProductForm.form.controls[ProductFieldNames.Type].setValue(type);

            this.createProductForm.form.get(ProductFieldNames.NominalValue).setValidators(validators);
            this.createProductForm.form.get(ProductFieldNames.NominalValue).updateValueAndValidity();
        });
    }

    ngOnDestroy(): void {
        if (this.operatorSubscription) {
            this.operatorSubscription.unsubscribe();
        }
    }

    ngOnInit(): void {
        this.initForm();
    }

    initForm(): void {
        this.createProductForm = new CreateProductForm();
        this.createProductForm.initForm();

        this.operatorName = this.authApiService.getAuthenticatedUser().email;
    }

    getFormMessages(controlLabel?: string, controlLength?: number): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel, controlLength);
    }

    validateForm(): boolean {
        return this.createProductForm.validateForm(this.getFormMessages);
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.fundProductsService.create(this.getInputModel())
                        .subscribe(response => {
                            if (response.status !== HttpStatusCodesEnum.Ok) {
                                this.alertsService.addAlert(`Could not create product. ${response.messages.join(', ')}`);
                            }
                            else {
                                const responseData = response.data as ICreateProductRespoonse;

                                const path = [
                                    PanelPaths.root,
                                    PanelPaths.children.settings.path,
                                    PanelPaths.children.settings.children.products.path,
                                    PanelPaths.children.settings.children.products.children.edit.path,
                                    responseData.id,
                                    responseData.code,
                                    PanelPaths.children.settings.children.products.children.edit.children.details.path
                                ].join('/');

                                this.router.navigate([path])
                                    .finally();

                                this.dialogRef.close(false);
                            }
                        });
                }
                break;
        }
    }
}

